import {
  Collapse,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useCarrierClasses,
  useCostKmStats,
  useModes,
} from '../../hooks/apiHooks';
import { Class } from '../../models/Class';
import { Mode } from '../../models/Mode';

interface CostPerKmStatsModalProps {
  carrierId: number | null | undefined;
  carrierName?: string;
  onClose: () => void;
  open: boolean;
}

const CostPerKmStatsModal: React.FC<CostPerKmStatsModalProps> = ({
  carrierId,
  carrierName,
  onClose,
  open,
}) => {
  const { costKmStats, isFetching, isLoading } = useCostKmStats(
    carrierId ?? 0,
    open
  );
  const { carrierClasses } = useCarrierClasses(carrierId ?? undefined);
  const { modes } = useModes(carrierId ?? undefined);

  const [filterOrgProv, setFilterOrgProv] = useState<string | undefined>();
  const [filterDestProv, setFilterDestProv] = useState<string | undefined>();
  const [filterModeId, setFilterModeId] = useState<number | undefined>();

  useEffect(() => {
    if (!open) {
      setFilterOrgProv(undefined);
      setFilterDestProv(undefined);
      setFilterModeId(undefined);
    }
  }, [open]);

  const { destProvinces, groupedByRoute, modeOptions, orgProvinces } =
    useMemo(() => {
      if (!costKmStats?.length) {
        return {
          destProvinces: [] as string[],
          groupedByRoute: [] as Array<{
            byMode: Record<
              string,
              Array<{ classId: number; [key: string]: unknown }>
            >;
            destProv: string;
            orgProv: string;
          }>,
          modeOptions: [] as Array<{ label: string; value: number }>,
          orgProvinces: [] as string[],
          routeKey: null as string | null,
        };
      }

      const orgSet = new Set<string>();
      const destSet = new Set<string>();
      const modesByCorridor: Record<string, Set<number>> = {};
      const allModeIds = new Set<number>();
      const byKey: Record<
        string,
        {
          byMode: Record<string, Array<unknown>>;
          destProv: string;
          orgProv: string;
        }
      > = {};

      for (const row of costKmStats) {
        orgSet.add(row.orgProv);
        destSet.add(row.destProv);
        allModeIds.add(row.modeId);

        const routeKey = `${row.orgProv}-${row.destProv}`;
        if (!modesByCorridor[routeKey]) {
          modesByCorridor[routeKey] = new Set();
        }
        modesByCorridor[routeKey].add(row.modeId);

        if (!byKey[routeKey]) {
          byKey[routeKey] = {
            byMode: {},
            destProv: row.destProv,
            orgProv: row.orgProv,
          };
        }
        const modeKey = row.modeId;
        if (!byKey[routeKey].byMode[modeKey]) {
          byKey[routeKey].byMode[modeKey] = [];
        }
        byKey[routeKey].byMode[modeKey].push(row);
      }

      const orgProvincesList = Array.from(orgSet).sort();
      const destProvincesList = Array.from(destSet).sort();
      const currentRouteKey =
        filterOrgProv && filterDestProv
          ? `${filterOrgProv}-${filterDestProv}`
          : null;
      const modeIdsForCorridor = currentRouteKey
        ? Array.from(modesByCorridor[currentRouteKey] || [])
        : Array.from(allModeIds);
      const modeOptionsList = modeIdsForCorridor
        .map((id) => {
          const mode = modes?.find((m: Mode) => m.id === id);
          return mode ? { label: mode.name ?? '-', value: id } : null;
        })
        .filter(Boolean) as Array<{ label: string; value: number }>;

      let routes = Object.values(byKey);
      if (filterOrgProv) {
        routes = routes.filter((r) => r.orgProv === filterOrgProv);
      }
      if (filterDestProv) {
        routes = routes.filter((r) => r.destProv === filterDestProv);
      }
      if (filterModeId != null) {
        routes = routes
          .map((r) => ({
            ...r,
            byMode: r.byMode[filterModeId]?.length
              ? { [filterModeId]: r.byMode[filterModeId] }
              : {},
          }))
          .filter((r) => Object.keys(r.byMode).length > 0);
      }

      return {
        destProvinces: destProvincesList,
        groupedByRoute: routes,
        modeOptions: modeOptionsList,
        orgProvinces: orgProvincesList,
      };
    }, [costKmStats, filterOrgProv, filterDestProv, filterModeId, modes]);

  const getModeName = (modeId: number) =>
    modes?.find((m: Mode) => m.id === modeId)?.name ?? '-';
  const getClassName = (classId: number) =>
    carrierClasses?.find((c: Class) => c.id === classId)?.name ?? '-';

  const getConfidenceChip = (sampleCount: number | undefined) => {
    if (sampleCount == null) return <Tag color="default">–</Tag>;
    const count = Number(sampleCount);
    let chip: React.ReactNode;
    if (count <= 4) chip = <Tag color="red">Very Low</Tag>;
    else if (count <= 9) chip = <Tag color="gold">Low</Tag>;
    else if (count <= 24) chip = <Tag color="blue">Moderate</Tag>;
    else chip = <Tag color="green">High</Tag>;
    return (
      <Tooltip overlayInnerStyle={{ textAlign: 'center' }} title={count}>
        {chip}
      </Tooltip>
    );
  };

  const getPriceVariationChip = (stddev: number | undefined) => {
    if (stddev == null) return <Tag color="default">–</Tag>;
    const s = Number(stddev);
    let chip: React.ReactNode;
    if (s < 0.1) chip = <Tag color="green">Stable</Tag>;
    else if (s < 0.3) chip = <Tag color="blue">Moderate</Tag>;
    else if (s < 0.6) chip = <Tag color="gold">High</Tag>;
    else chip = <Tag color="red">Volatile</Tag>;
    return (
      <Tooltip overlayInnerStyle={{ textAlign: 'center' }} title={s.toFixed(4)}>
        {chip}
      </Tooltip>
    );
  };

  const buildTableConfig = (
    rows: Array<{
      classId: number;
      avg?: number;
      min?: number;
      max?: number;
      stddev?: number;
      sampleCount?: number;
    }>
  ) => {
    const byClass: Record<
      number,
      {
        classId: number;
        avg?: number;
        min?: number;
        max?: number;
        stddev?: number;
        sampleCount?: number;
      }
    > = {};
    for (const r of rows) {
      byClass[r.classId] = r;
    }
    const classIds = Object.keys(byClass)
      .map(Number)
      .sort((a, b) => a - b);

    const metricColumns = [
      {
        dataIndex: 'avg',
        key: 'avg',
        render: (value: number | undefined) =>
          value != null ? `$${Number(value).toFixed(2)}` : '–',
        title: 'Avg Cost/km',
      },
      {
        dataIndex: 'stddev',
        key: 'stddev',
        render: (value: number | undefined) => getPriceVariationChip(value),
        title: 'Price Variation',
      },
      {
        dataIndex: 'sampleCount',
        key: 'sampleCount',
        render: (value: number | undefined) => getConfidenceChip(value),
        title: 'Confidence',
      },
    ];

    const columns = [
      { dataIndex: 'class', key: 'class', title: 'Vehicle Types', width: 300 },
      ...metricColumns,
    ];

    const dataSource = classIds.map((classId) => {
      const row = byClass[classId];
      return {
        avg: row.avg,
        class: getClassName(classId),
        key: classId,
        sampleCount: row.sampleCount,
        stddev: row.stddev,
      };
    });

    return { columns, dataSource };
  };

  const getAvailableModeIdsForCorridor = (
    org: string | undefined,
    dest: string | undefined
  ) => {
    if (!costKmStats?.length) return [];
    if (org && dest) {
      const ids = new Set<number>();
      for (const row of costKmStats) {
        if (row.orgProv === org && row.destProv === dest) ids.add(row.modeId);
      }
      return Array.from(ids);
    }
    const allIds = new Set<number>();
    for (const row of costKmStats) allIds.add(row.modeId);
    return Array.from(allIds);
  };

  const handleOriginChange = (value: string | undefined) => {
    setFilterOrgProv(value);
    const availableModes = getAvailableModeIdsForCorridor(
      value,
      filterDestProv
    );
    if (filterModeId != null && !availableModes.includes(filterModeId)) {
      setFilterModeId(undefined);
    }
  };

  const handleDestChange = (value: string | undefined) => {
    setFilterDestProv(value);
    const availableModes = getAvailableModeIdsForCorridor(filterOrgProv, value);
    if (filterModeId != null && !availableModes.includes(filterModeId)) {
      setFilterModeId(undefined);
    }
  };

  const handleModeChange = (value: number | undefined) => {
    setFilterModeId(value);
  };

  return (
    <Modal
      centered
      destroyOnClose
      footer={
        <div>
          <div>
            <Typography.Text style={{ fontSize: 12 }} type="secondary">
              Price Variation (how it differs from the average): Stable
              (&lt;0.1), Moderate (0.1 - 0.3), High (0.3 - 0.6), Volatile (0.6+)
            </Typography.Text>
          </div>
          <div>
            <Typography.Text style={{ fontSize: 12 }} type="secondary">
              Confidence (based on how many samples): Very Low (0 - 4), Low (5 -
              9), Moderate (10 - 24), High (25+)
            </Typography.Text>
          </div>
        </div>
      }
      open={open}
      title={`Cost Per KM Stats${carrierName ? ` for ${carrierName}` : ''}`}
      width={800}
      onCancel={onClose}
    >
      {!isLoading && !!costKmStats?.length && (
        <div style={{ marginBottom: 8 }}>
          <Space>
            <span>Filter by:</span>
            <Select
              allowClear
              optionFilterProp="label"
              options={orgProvinces.map((p) => ({ label: p, value: p }))}
              placeholder="Origin"
              style={{ minWidth: 160 }}
              value={filterOrgProv}
              onChange={handleOriginChange}
            />
            <Select
              allowClear
              optionFilterProp="label"
              options={destProvinces.map((p) => ({
                label: p,
                value: p,
              }))}
              placeholder="Destination"
              style={{ minWidth: 160 }}
              value={filterDestProv}
              onChange={handleDestChange}
            />
            <Select
              allowClear
              optionFilterProp="label"
              options={modeOptions}
              placeholder="Mode"
              style={{ minWidth: 160 }}
              value={filterModeId}
              onChange={handleModeChange}
            />
          </Space>
        </div>
      )}
      <div style={{ minHeight: 450 }}>
        {isLoading || (isFetching && !groupedByRoute.length) ? (
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 450,
              textAlign: 'center',
            }}
          >
            <Spin size="large" />
          </div>
        ) : !groupedByRoute.length ? (
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 450,
            }}
          >
            <p>No cost per KM stats available</p>
          </div>
        ) : (
          <div style={{ maxHeight: 450, overflow: 'auto' }}>
            {groupedByRoute.map((route) => {
              const filteredByMode =
                filterModeId != null
                  ? {
                      [filterModeId]: route.byMode[filterModeId] ?? [],
                    }
                  : route.byMode;
              const collapseItems = Object.entries(filteredByMode).map(
                ([modeIdStr, rows]) => {
                  const modeId = Number(modeIdStr);
                  const typedRows = rows as Array<{
                    classId: number;
                    avg?: number;
                    min?: number;
                    max?: number;
                    stddev?: number;
                    sampleCount?: number;
                  }>;
                  const { columns, dataSource } = buildTableConfig(typedRows);
                  return {
                    children: (
                      <Table
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        size="small"
                      />
                    ),
                    key: modeId,
                    label: getModeName(modeId),
                  };
                }
              );
              return (
                <div key={`${route.orgProv}-${route.destProv}`}>
                  <h2>
                    {route.orgProv} – {route.destProv}
                  </h2>
                  <Collapse
                    defaultActiveKey={collapseItems.map((item) => item.key)}
                    items={collapseItems}
                    size="small"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CostPerKmStatsModal;
